import React, { useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useHistory, useParams } from 'react-router';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Container from '@material-ui/core/Container';
import Link from '@material-ui/core/Link';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import VariableTextField from '../../components/variableTextField';
import VariableSelector from '../../components/variableSelector';
import Dialog from '../../components/dialog/member';
import { useAPI } from '../../utils/api';
import { useDialog } from '../../utils/dialog';
import CombineSelector from '../../components/combineSelector';
import { useAuth } from '../../utils/auth';
import { MODE } from '../../utils/mode';

let relatedMember = [];

const MemberDetailPage = () => {
  const auth = useAuth();
  const api = useAPI();
  const dialog = useDialog();
  const history = useHistory();

  const { memberID, mode } = useParams();

  const memberForm = useRef();

  const [isUnmounted, setIsUnmounted] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [cityList, setCityList] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [memberTypes, setMemberTypes] = useState([]);
  const [member, setMember] = useState();
  const [shouldPageUpdated, setShouldPageUpdated] = useState(false);
  const [rateTables, setRateTables] = useState([]);
  const [openMonthlyDialog, setMonthlyDialog] = useState(false);
  const [displayPaymentMethod, setDisplayPaymentMethod] = useState('　');
  const [
    shouldDisplayPaymentMethodUpdate,
    setShouldDisplayPaymentMethodUpdate,
  ] = useState(false);
  const [bankCodes, setBankCodes] = useState([]);
  const [refreshDialog, setRefreshDialog] = useState(false);

  const parseMember = (data) => {
    if (!data) return;

    const abbreviation = data.get('abbreviation');
    const id = data.get('id');
    const title = data.get('title');
    const personInCharge = data.get('personInCharge');
    const paymentCondition = data.get('paymentCondition');
    const industryType = data.get('industryType');
    const memberType = data.get('memberType');
    const city = data.get('city');
    const district = data.get('district');
    const address = data.get('address');
    const website = data.get('website');
    const email = data.get('email');
    const password = data.get('password');
    const areaCode = data.get('areaCode');
    const telPhone = data.get('telPhone');
    const extensionNumber = data.get('extensionNumber');
    const telPhoneAll = data.get('telPhoneAll');
    const mobile = data.get('mobile');
    const remark = data.get('remark');
    const paymentMethod = data.get('paymentMethod');
    const accountingInCharge = data.get('accountingInCharge');
    const accountReceivable = data.get('accountReceivable');
    const disscount = data.get('disscount');
    const taxNumber = data.get('taxNumber');
    const rateTable = data.get('rateTable');
    const requestDate = data.get('requestDate');
    const checkoutDate = data.get('checkoutDate');
    const bankCode = data.get('bankCode');
    const bankName = data.get('bankName');
    const bankAccount = data.get('bankAccount');
    const matchMonthlyFee = data.get('matchMonthlyFee');
    const preOrder = data.get('preOrder');
    const reference = data.get('reference') || [];
    const member_id = memberID;

    const memberData = {
      abbreviation,
      id,
      member_id,
      title,
      personInCharge,
      paymentCondition,
      industryType,
      memberType,
      email,
      password,
      city,
      district,
      address,
      website,
      areaCode,
      telPhone,
      extensionNumber,
      telPhoneAll,
      mobile,
      remark,
      paymentMethod,
      accountingInCharge,
      accountReceivable,
      disscount,
      taxNumber,
      rateTable,
      requestDate,
      checkoutDate,
      bankCode,
      bankName,
      bankAccount,
      matchMonthlyFee,
      preOrder,
      reference,
    };

    return memberData;
  };

  const submit = (event) => {
    event.preventDefault();

    if (mode === MODE.VIEW) {
      openEditMode();
      return;
    }

    const formData = new FormData(event.currentTarget);

    const data = parseMember(formData);

    const validateResult = validateData(data);
    if (validateResult) {
      dialog.openAlert({ message: validateResult });
      return;
    }

    if (mode === MODE.EDIT) {
      api
        .updateMemberDetail({ data })
        .then(() => {
          fetchMemberDetail();
          openViewMode();
        })
        .catch(() => {});
    } else if (mode === MODE.NEW) {
      api
        .createNewMember({ data })
        .then(() => {
          dialog.openAlert({
            message: '新增成功。',
            replaceTo: '/member/list',
          });
        })
        .catch(() => {});
    }
  };

  const selectPaymentMethod = (event) => {
    for (const element of paymentMethods) {
      if (element.value === event.target.value) {
        setDisplayPaymentMethod(element.text || '　');
      }
    }
  };

  const openPreOrder = () => {
    const newPath = generatePath(`/member/preorder/${memberID || ''}`);
    history.replace({ pathname: newPath });
  };

  const openEditMode = () => {
    const newPath = generatePath(`/member/${MODE.EDIT}/${memberID || ''}`);
    history.replace({ pathname: newPath });
  };

  const openViewMode = () => {
    const newPath = generatePath(`/member/${MODE.VIEW}/${memberID || ''}`);
    history.replace({ pathname: newPath });
  };

  const fetchMemberDetail = useCallback(() => {
    api
      .getMemberDetail({ memberID })
      .then((result) => {
        setShouldPageUpdated(true);
        setMember(result);
        setDisplayPaymentMethod(result.paymentCondition || '　');
        relatedMember = result.reference;
      })
      .catch(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberID]);

  const fetchRelatedMember = useCallback(() => {
    api
      .getRelatedMember({ memberID })
      .then((result) => {
        setShouldPageUpdated(true);

        relatedMember = result;
        member.reference = result;
      })
      .catch(() => {});

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberID, member]);

  const addAccountCode = (a, b) => {
    api.addRelatedMember(a, b);
  };

  const removeAccountCode = (a, b) => {
    api.removeRelatedMember(a, b);
  };

  useEffect(() => {
    if (mode === MODE.VIEW) {
      if (memberID) setShouldPageUpdated(true);
      else if (!(auth && auth.user && auth.user.memberID)) history.replace('/');
      else {
        const newPath = generatePath(
          `/member/${MODE.VIEW}/${auth.user.memberID || ''}`
        );
        history.replace({ pathname: newPath });
      }
    } else if (mode === MODE.EDIT) {
      if (!memberID) history.replace('/');
      setShouldPageUpdated(true);
    } else if (mode === MODE.NEW) {
      setShouldPageUpdated(true);
    } else {
      history.replace('/');
    }
  }, [mode, history, memberID, auth]);

  useEffect(() => {
    if (isUnmounted) return;

    api
      .getDropdownCitys()
      .then((result) => {
        if (!isUnmounted) {
          setCityList(result);
        }
      })
      .catch(() => {});

    api
      .getDropdownPaymentMethods({ isCreatingMember: true })
      .then((result) => {
        if (!isUnmounted) {
          setPaymentMethods(result);
        }
      })
      .catch(() => {});

    api
      .getIndustryTypes()
      .then((result) => {
        if (!isUnmounted) {
          setIndustryTypes(result);
        }
      })
      .catch(() => {});

    api
      .getMemberTypes()
      .then((result) => {
        if (!isUnmounted) {
          setMemberTypes(result);
        }
      })
      .catch(() => {});

    api
      .getDropdownBankCodes()
      .then((result) => {
        if (!isUnmounted) {
          setBankCodes(result);
        }
      })
      .catch(() => {});

    api
      .getDropdownShippingRates()
      .then((result) => {
        if (!isUnmounted) {
          setRateTables(result);
        }
      })
      .catch(() => {});

    return () => {
      setIsUnmounted(true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (shouldPageUpdated && mode === MODE.NEW) {
      setMember({});
    }

    return () => {
      setShouldPageUpdated(false);
    };
  }, [mode, shouldPageUpdated]);

  useEffect(() => {
    setShouldDisplayPaymentMethodUpdate(true);
  }, [displayPaymentMethod]);

  useEffect(() => {
    setShouldDisplayPaymentMethodUpdate(false);
  }, [shouldDisplayPaymentMethodUpdate]);

  useEffect(() => {
    if (isMounted) return;

    if (mode === MODE.VIEW || mode === MODE.EDIT) {
      if (memberID && memberID !== 'undefined') {
        fetchMemberDetail();
        setIsMounted(true);
      }
    } else if (mode === MODE.NEW) setIsMounted(true);
  }, [fetchMemberDetail, isMounted, memberID, mode]);

  useEffect(() => {
    if (!refreshDialog) return;

    fetchRelatedMember(memberID);
    setRefreshDialog(false);
  }, [refreshDialog, memberID, fetchRelatedMember]);

  return (
    <>
      {!shouldPageUpdated && member ? (
        <>
          <Box ref={memberForm} component="form" noValidate onSubmit={submit}>
            {/* 會員資料 */}
            <Paper
              elevation={4}
              sx={{
                paddingTop: { xs: 1, md: 2 },
                paddingBottom: { xs: 2, md: 4 },
                marginBottom: 4,
                maxWidth: '900px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <Container>
                <Typography variant="h6" marginBottom={4}>
                  會員資料
                </Typography>

                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="abbreviation"
                      label="會員簡稱"
                      text={member.abbreviation}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      locked
                      name="id"
                      label="會員編號"
                      text={
                        mode === MODE.NEW ? '系統自動帶入' : member.account_code
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableSelector
                      mode={mode === MODE.NEW ? mode : MODE.VIEW}
                      name="memberType"
                      label="會員分類"
                      defaultValue={member.member_type_code}
                      options={memberTypes}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableSelector
                      mode={mode === MODE.NEW ? mode : MODE.VIEW}
                      name="industryType"
                      label="產業類型"
                      defaultValue={member.industry_type_code}
                      options={industryTypes}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="title"
                      label="中文名稱"
                      text={member.company_name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="personInCharge"
                      label="負責人"
                      text={member.owner}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {!shouldDisplayPaymentMethodUpdate ? (
                      <VariableTextField
                        mode={MODE.VIEW}
                        name="paymentCondition"
                        label="付款條件"
                        text={displayPaymentMethod}
                        locked
                      />
                    ) : undefined}
                  </Grid>

                  <CombineSelector
                    mode={mode}
                    optionsList={{
                      firstName: 'city',
                      firstLabel: '縣市',
                      options: cityList,
                      secondName: 'district',
                      secondLabel: '區域',
                      firstText: member.city,
                      secondText: member.district,
                      firstValue: member.city,
                      secondValue: member.districtID,
                    }}
                    secondOptionsKeyName="districts"
                  />

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="address"
                      label="地址"
                      text={member.address}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="website"
                      label="公司網站"
                      text={member.website}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="email"
                      label="電子郵件"
                      text={member.email}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      locked={mode === MODE.EDIT}
                      name="password"
                      label="密碼"
                      text={
                        mode === MODE.NEW ? '系統自動帶入' : member.password
                      }
                    />
                  </Grid>
                  {mode !== MODE.VIEW ? (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      container
                      alignItems="center"
                      sx={{
                        position: 'relative',
                        marginTop: '8px',
                        paddingTop: '18px !important',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography
                        position="absolute"
                        fontWeight={mode === MODE.EDIT ? '' : 'bold'}
                        fontSize="8px"
                        top="0"
                        color={
                          mode === MODE.EDIT ? 'rgba(0, 0, 0, 0.6)' : '#777'
                        }
                      >
                        公司電話
                      </Typography>
                      <Grid item xs={2}>
                        <VariableTextField
                          mode={mode}
                          name="areaCode"
                          type="tel"
                          text={member.areaCode}
                          autoComplete="none"
                          textAlign="center"
                        />
                      </Grid>
                      <div>-</div>
                      <Grid item xs={5} sx={{ paddingLeft: 0 }}>
                        <VariableTextField
                          mode={mode}
                          name="telPhone"
                          type="tel"
                          text={member.telPhone}
                          autoComplete="none"
                          textAlign="center"
                        />
                      </Grid>
                      <div>#</div>
                      <Grid item xs={3}>
                        <VariableTextField
                          mode={mode}
                          name="extensionNumber"
                          type="tel"
                          text={member.extensionNumber}
                          autoComplete="none"
                          textAlign="center"
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <VariableTextField
                        mode={mode}
                        name="telPhoneAll"
                        label="公司電話"
                        text={`(${member.areaCode}) ${member.telPhone} # ${member.extensionNumber}`}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="mobile"
                      label="行動電話"
                      type="tel"
                      text={member.mobile}
                      autoComplete="none"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={mode}
                      name="remark"
                      label="會員備註"
                      text={member.remark}
                    />
                  </Grid>
                </Grid>
              </Container>
            </Paper>

            {/* 帳務資料 */}
            <Paper
              elevation={4}
              sx={{
                paddingTop: { xs: 1, md: 2 },
                paddingBottom: { xs: 2, md: 4 },
                marginBottom: 4,
                maxWidth: '900px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <Container>
                <Typography variant="h6" marginBottom={4}>
                  帳務資料
                </Typography>
                <Grid container spacing={{ xs: 1, md: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <VariableSelector
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="paymentMethod"
                      label="付款方式"
                      defaultValue={member.payment_method}
                      onChange={selectPaymentMethod}
                      options={paymentMethods}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      locked={auth.user.isAdmin ? false : true}
                      name="accountingInCharge"
                      label="負責會計"
                      text={member.accountingInCharge}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="accountReceivable"
                      label="應收帳款科目"
                      text={member.accountReceivable}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="disscount"
                      label="折數"
                      text={member.disscount}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="taxNumber"
                      label="公司統編"
                      text={member.taxNumber}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableSelector
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="rateTable"
                      label="費率表"
                      defaultValue={member.shippingRateListID}
                      options={rateTables}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="requestDate"
                      label="請款日"
                      text={member.checkoutDate} //member.requestDate
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      locked={auth.user.isAdmin ? false : true}
                      name="checkoutDate"
                      label="結帳日"
                      text={member.requestDate} //member.checkoutDate
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableSelector
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="bankCode"
                      label="銀行代碼"
                      options={bankCodes}
                      defaultValue={member.bankCode}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="bankName"
                      label="銀行名稱"
                      text={member.bankName}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="bankAccount"
                      label="銀行帳戶"
                      text={member.bankAccount}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="feeMonthly"
                      label="符合的月結手續費"
                      text={member.feeMonthly}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VariableTextField
                      mode={
                        mode !== MODE.VIEW && auth.user.isAdmin
                          ? MODE.EDIT
                          : MODE.VIEW
                      }
                      name="preOrder"
                      label="預購"
                      text={
                        mode === MODE.NEW
                          ? ''
                          : mode === MODE.EDIT
                          ? member.avalidPoint || ''
                          : `可用點數 ${member.avalidPoint || 0}`
                      }
                    />
                    {mode !== MODE.NEW ? (
                      <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={openPreOrder}
                        // startIcon={<EditIcon />}
                        sx={{ color: 'white' }}
                      >
                        查看訂單明細
                      </Button>
                    ) : undefined}
                  </Grid>

                  {mode === MODE.NEW ? (
                    ''
                  ) : (
                    <Grid item xs={12} sm={6}>
                      <VariableTextField
                        name="reference"
                        label="月結手續費_關聯會員"
                        text={
                          (member &&
                            member.reference &&
                            member.reference
                              .map((ele) => ele.account_code)
                              .join('、')) ||
                          '查無資料'
                        }
                      />
                      <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        color="success"
                        onClick={() => setMonthlyDialog(true)}
                        sx={{ color: 'white' }}
                      >
                        查看及調整
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Container>
            </Paper>

            <Grid
              container
              spacing={3}
              sx={{ margin: 'auto', width: '100%', justifyContent: 'center' }}
            >
              {mode !== MODE.VIEW ? (
                <>
                  <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                    <Link
                      underline="none"
                      href={
                        mode === MODE.NEW
                          ? '/#/member/list'
                          : `/#/member/${MODE.VIEW}/${memberID}`
                      }
                    >
                      <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        color="primary"
                        startIcon={<ArrowBackIcon />}
                      >
                        返回
                      </Button>
                    </Link>
                  </Grid>
                  <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="success"
                      sx={{ color: 'white' }}
                    >
                      {mode === MODE.NEW ? '新增' : '儲存'}
                    </Button>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      color="primary"
                    >
                      編輯會員
                    </Button>
                  </Grid>
                  <Grid item xs={10} sm={4} sx={{ maxWidth: '200px' }}>
                    <Link underline="none" href={`/#/contact/list/${memberID}`}>
                      <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        color="primary"
                      >
                        常用聯絡人
                      </Button>
                    </Link>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </>
      ) : undefined}

      <Dialog
        open={openMonthlyDialog}
        title="月結手續費_關聯會員設定"
        onClose={() => setMonthlyDialog(false)}
        onConfirm={() => setMonthlyDialog(false)}
        onAdd={(a) => {
          addAccountCode(memberID, a);
          setRefreshDialog(true);
        }}
        min={290}
      >
        {relatedMember.map((data, index) => (
          <Paper
            key={String(index)}
            elevation={2}
            sx={{
              minWidth: 200,
              height: '100%',
              backgroundColor: '#f0f0f0',
              marginBottom: 2,
            }}
          >
            <Grid container>
              <Grid item xs={9} sm={9}>
                <Typography variant="body1" marginTop={1}>
                  {data.account_code}
                </Typography>
              </Grid>
              <Grid item xs={3} sm={3}>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => {
                    removeAccountCode(memberID, data.account_code);
                    setRefreshDialog(true);
                  }}
                >
                  刪除
                </Button>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Dialog>
    </>
  );
};

export default MemberDetailPage;

const validateData = (data) => {
  if (!data.abbreviation || data.abbreviation.length === 0)
    return '請輸入會員簡稱';
  if (!data.memberType || data.memberType.length === 0) return '請選擇會員分類';
  if (!data.industryType || data.industryType.length === 0)
    return '請選擇產業類型';
  if (!data.title || data.title.length === 0) return '請輸入中文名稱';
  if (!data.personInCharge || data.personInCharge.length === 0)
    return '請輸入負責人';
  if (!data.city || data.city.length === 0) return '請選擇縣市';
  if (!data.district || data.district.length === 0) return '請選擇區域';
  if (!data.address || data.address.length === 0) return '請輸入地址';
  if (!data.email || data.email.length === 0) return '請輸入電子郵件';
  if (
    (!data.telPhone || data.telPhone.length === 0) &&
    (!data.mobile || data.mobile.length === 0)
  ) {
    return '公司電話或行動電話請擇一輸入';
  }
  if (!data.paymentCondition || data.paymentCondition.length === 0)
    return '請選擇付款方式';
  if (!data.rateTable || data.rateTable.length === 0) return '請選擇費率表';
  if (!data.requestDate || data.requestDate.length === 0) return '請輸入請款日';
  if (!data.checkoutDate || data.checkoutDate.length === 0)
    return '請輸入結帳日';
};
